import { useState, useEffect, useRef } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useSaveWeeklySummary } from '../mutations/useSaveWeeklySummary.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useUIStore } from '../store/uiStore.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'
import { goalKey, pillarIndexByName, timeAgo } from '../data/metaKeys.js'
import { getAuthoritative } from '../data/weeklySummaryOverrides.js'
import CarryForwardButton from './CarryForwardButton.jsx'

/**
 * WeeklySummary — per-goal three-field weekly update.
 *
 * Fields (matching q2-goals.html lines 1127–1129):
 *   🟣 TLDR   — one sentence: where does this goal stand this week
 *   🟢 Wins   — what went well, milestones hit
 *   🟡 Flags  — what's at risk, blocked, or needs leadership attention
 *
 * Stored under meta.weeklySummary[`p${pi}-g${gi}`] as:
 *   { tldr, wins, flags, savedAt, savedBy }
 *
 * Only shown on top-level goals. Sub-goals use the simpler `update`
 * field on the goal data itself (unchanged from before).
 *
 * Writes are optimistic and debounced via local-draft state (save on blur).
 */
export default function WeeklySummary({ pillarName, goalIndex }) {
  const currentUser = useUIStore((s) => s.currentUser)
  const setEditingGoalKey = useUIStore((s) => s.setEditingGoalKey)
  const clearEditingGoalKey = useUIStore((s) => s.clearEditingGoalKey)
  const { data: goalsData } = useGoalsData()
  const { data: meta } = useMetaData()
  const saveSummary = useSaveWeeklySummary()
  const readOnly = useIsReadOnly()

  const pi = pillarIndexByName(goalsData, pillarName)
  const key = pi >= 0 ? goalKey(pi, goalIndex) : null
  // Pull server state first, then let the override helper decide whether
  // a recently-saved local entry should shadow it. The helper compares
  // savedAt timestamps — only shadows when the override is strictly
  // newer than the server entry, so real edits from other users always
  // win once they propagate.
  const serverEntry = (key && meta.weeklySummary?.[key]) || null
  const override = key ? getAuthoritative(key, serverEntry) : null
  const saved = override ?? serverEntry ?? {}

  // Local draft state — so typing doesn't fire a save on every keystroke.
  // Save happens on blur.
  const [draft, setDraft] = useState({
    tldr: saved.tldr || '',
    wins: saved.wins || '',
    flags: saved.flags || '',
  })
  const [editing, setEditing] = useState(null) // 'tldr' | 'wins' | 'flags' | null

  // When the server value changes and we're NOT mid-edit, re-sync the draft.
  useEffect(() => {
    if (editing) return
    setDraft({
      tldr: saved.tldr || '',
      wins: saved.wins || '',
      flags: saved.flags || '',
    })
  }, [saved.tldr, saved.wins, saved.flags, editing])

  // While editing any of the three fields, broadcast that we're editing
  // THIS goal so other clients see a pulsing avatar next to its title.
  useEffect(() => {
    if (!editing || pi < 0) return
    setEditingGoalKey(`${pillarName}:${goalIndex}`)
    return () => clearEditingGoalKey()
  }, [editing, pillarName, goalIndex, pi, setEditingGoalKey, clearEditingGoalKey])

  // persistField — pure "send the patch" helper. Does NOT touch the
  // editing state, so it can be called by both blur (which also
  // closes the editor) and the debounced autosave (which keeps the
  // editor open so the user can keep typing).
  const persistField = (field, newValue) => {
    if (!key) return
    if (newValue === (saved[field] || '')) return
    const patch = {
      [field]: newValue,
      savedAt: Date.now(),
      savedBy: currentUser || 'Unknown',
    }
    saveSummary.mutate({ goalKey: key, patch })
  }

  const saveField = (field) => {
    persistField(field, draft[field])
    setEditing(null)
  }

  // Autosave-while-typing safety net.
  //
  // Historically the only save trigger was onBlur. If the user closed
  // the tab, the auto-update banner reloaded the page, or focus got
  // lost without a blur event firing for any reason, their typed text
  // was never persisted. This runs a save 2 seconds after the last
  // keystroke so typed-but-unblurred content survives interruptions.
  //
  // Patches are idempotent server-side (latest value wins via a
  // merge), so overlapping autosaves + blur saves are safe.
  //
  // Flush-on-unmount: if the user switches pillars / closes the panel
  // before the 2s debounce fires, we run one last save so no text is
  // lost. Accomplished by stashing the latest draft+editing in refs
  // and reading them from the cleanup function.
  const activeDraftValue = editing ? draft[editing] : null
  const latestDraftRef = useRef(draft)
  const latestEditingRef = useRef(editing)
  latestDraftRef.current = draft
  latestEditingRef.current = editing
  useEffect(() => {
    if (!editing) return
    const timer = setTimeout(() => {
      persistField(editing, draft[editing])
    }, 2000)
    return () => clearTimeout(timer)
    // persistField is stable enough — we deliberately exclude it from
    // deps so the timer only resets on actual content/field changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftValue, editing])
  useEffect(() => {
    // Component-unmount flush: save whatever draft value is active when
    // the card goes away. Runs only on unmount (empty dep array) and
    // reads from refs so the closure stays fresh.
    return () => {
      const field = latestEditingRef.current
      if (!field) return
      const value = latestDraftRef.current?.[field]
      if (value === undefined) return
      persistField(field, value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fields = [
    {
      field: 'tldr',
      label: 'TLDR',
      dot: '🟣',
      color: '#9333ea',
      bg: '#faf5ff',
      border: '#e9d5ff',
      hint: 'Click to add a multi-sentence status summary',
      placeholder:
        "Where this goal stands right now — 2–3 sentences on progress, what changed this week, and the path to target. This is the text that feeds your executive summary, so make it substantive.",
      minH: 72,
    },
    {
      field: 'wins',
      label: 'Wins',
      dot: '🟢',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      hint: 'Click to add the week\'s wins',
      placeholder:
        "What went well this week? Milestones hit, deals closed, work shipped, people unblocked. Specifics help the exec summary tell a real story.",
      minH: 64,
    },
    {
      field: 'flags',
      label: 'Flags / Blockers / Risks',
      dot: '🟡',
      color: '#d97706',
      bg: '#fffbeb',
      border: '#fde68a',
      hint: 'Click to flag blockers, risks, or asks',
      placeholder:
        "What's at risk, blocked, or needs leadership attention? Be concrete — who needs to unblock what, and by when.",
      minH: 64,
    },
  ]

  const hasAnyContent = saved.tldr || saved.wins || saved.flags

  // Carry-forward: pull last snapshot's weekly summary for this goal
  // and, if the current entry is entirely empty, offer a button to
  // bring last week's content forward as a starting point. Shown only
  // on the panel header, not per-field, to avoid button clutter.
  const lastSnapshotWeek = meta?.lastSnapshotWeek
  const priorEntry = key && lastSnapshotWeek
    ? meta?.weeklyHistory?.[lastSnapshotWeek]?.weeklySummary?.[key]
    : null
  const priorHasContent = !!(priorEntry && (priorEntry.tldr || priorEntry.wins || priorEntry.flags))
  const showCarryForward = !readOnly && !hasAnyContent && priorHasContent

  const applyCarryForward = () => {
    if (!key || !priorEntry) return
    // Carry-forward writes all three fields at once — but since the
    // card is empty by definition (the button only shows when there's
    // no current content), a full-entry write has no clobber risk.
    // Could use a patch here too; entry form is just as safe.
    const patch = {
      tldr: priorEntry.tldr || '',
      wins: priorEntry.wins || '',
      flags: priorEntry.flags || '',
      savedAt: Date.now(),
      savedBy: currentUser || 'Unknown',
    }
    saveSummary.mutate({ goalKey: key, patch })
  }

  return (
    <div
      data-tour="weekly-summary"
      style={{
        background: '#f9fafb',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: 12,
        marginTop: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--muted)',
            textTransform: 'uppercase',
            letterSpacing: 0.08,
          }}
        >
          📋 Weekly Summary
        </div>
        {showCarryForward && (
          <CarryForwardButton
            label="↩ Bring over last week"
            title="Copy last week's TLDR, Wins, and Flags into this panel"
            preview={[priorEntry?.tldr, priorEntry?.wins, priorEntry?.flags].filter(Boolean).join(' · ')}
            onApply={applyCarryForward}
          />
        )}
        {saved.savedAt && (
          <div style={{ fontSize: 10, color: 'var(--muted-light)' }}>
            {saved.savedBy ? `${saved.savedBy} · ` : ''}{timeAgo(saved.savedAt)}
          </div>
        )}
      </div>

      {fields.map((f) => {
        const isEditing = editing === f.field
        const value = draft[f.field]
        const empty = !value

        return (
          <div
            key={f.field}
            style={{
              marginBottom: 8,
              padding: '8px 10px',
              background: f.bg,
              border: `1px solid ${f.border}`,
              borderRadius: 8,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: f.color,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginBottom: 4,
              }}
            >
              {f.dot} {f.label}
            </div>

            {isEditing ? (
              <textarea
                autoFocus
                value={value}
                onChange={(e) => setDraft((d) => ({ ...d, [f.field]: e.target.value }))}
                onBlur={() => saveField(f.field)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setDraft((d) => ({ ...d, [f.field]: saved[f.field] || '' }))
                    setEditing(null)
                  }
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    saveField(f.field)
                  }
                }}
                placeholder={f.placeholder}
                style={{
                  width: '100%',
                  minHeight: f.minH,
                  fontSize: 12,
                  fontFamily: 'inherit',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--accent)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  background: '#fff',
                  resize: 'vertical',
                  lineHeight: 1.5,
                  outline: 'none',
                }}
              />
            ) : (
              <div
                onClick={readOnly ? undefined : () => setEditing(f.field)}
                style={{
                  fontSize: 12,
                  color: empty ? 'var(--muted-light)' : 'var(--text-secondary)',
                  fontStyle: empty ? 'italic' : 'normal',
                  lineHeight: 1.5,
                  cursor: readOnly ? 'default' : 'pointer',
                  minHeight: empty ? 20 : undefined,
                  padding: '2px 0',
                  whiteSpace: 'pre-wrap',
                }}
                title={readOnly ? undefined : (empty ? f.placeholder : 'Click to edit')}
              >
                {empty
                  ? (readOnly ? '—' : f.hint)
                  : value}
              </div>
            )}
          </div>
        )
      })}

    </div>
  )
}
